import { Button, ButtonProps } from "@progress/kendo-react-buttons";
import classNames from "classnames";
import * as React from "react";
import { PropsWithChildren, ReactElement, useCallback, useMemo } from "react";
import { If, Then } from "react-if";

import "./collapse.scss";

interface CardProps {
  active?: boolean;
  expand?: boolean;
}

const CardContext = React.createContext({} as CardProps);

export const CollapseCardActionContext = React.createContext<
  | {
      onClick: () => void;
    }
  | undefined
>(undefined);

export const CollapseTitle: React.FC<PropsWithChildren> = props => {
  return (
    <div className="tlx-collapse-container--inner-title">{props.children} </div>
  );
};
export const CollapseBody: React.FC<PropsWithChildren> = props => {
  return <>{props.children} </>;
};
export const CollapseContainer: React.FC<
  PropsWithChildren &
    Props & {
      style?: React.CSSProperties;
      className?: string;
      title?: React.ReactNode;
    }
> = props => {
  return (
    <div
      className={`tlx-v3-custom-collapse ${props.className || ""}`}
      style={props.style || {}}
    >
      <CollapseCard
        active={props.active}
        expand={props.expand}
        disabled={props.disabled}
        buttonMode={props.buttonMode}
        enableClickTitle={props.enableClickTitle}
        label={props.label}
        wrapBody={props.wrapBody}
      >
        {props.title && <CollapseTitle>{props.title} </CollapseTitle>}
        <CollapseBody>{props.children}</CollapseBody>
      </CollapseCard>
    </div>
  );
};

interface Props {
  active?: boolean;
  expand?: boolean;
  disabled?: boolean;
  buttonMode?: ButtonProps["fillMode"];
  enableClickTitle?: boolean;
  /** Accessible label for the toggle button (e.g. "Evidence And Timeline"). */
  label?: string;
  /**
   * Wraps the body in an `id`-bearing container referenced by the toggle's
   * `aria-controls`. Defaults to false because some consumers rely on
   * `.tlx-collapse-container`'s own flex/gap layout for spacing between body
   * children — an unconditional wrapper would collapse that gap.
   */
  wrapBody?: boolean;
}
const CollapseCard: React.FC<PropsWithChildren<Props>> = ({
  children,
  active = false,
  expand = false,
  disabled = false,
  buttonMode = "outline",
  enableClickTitle = false,
  label,
  wrapBody = false,
}) => {
  const [show, setShow] = React.useState<boolean>(expand);
  const [isActive, setActive] = React.useState<boolean>(active);
  const bodyId = React.useId();
  const ChildElements = React.Children.toArray(children) as ReactElement[];

  const TitleChild = ChildElements.find(ele => ele.type === CollapseTitle);
  const BodyChild = ChildElements.find(ele => ele.type === CollapseBody);

  const onClick = useCallback(() => {
    setShow(s => !s);
    setActive(a => !a);
  }, []);

  const onButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      // With `enableClickTitle` the container has its own `onClick`, and the
      // updater setters above make a propagating click toggle twice.
      event.stopPropagation();
      onClick();
    },
    [onClick],
  );

  const addClickTitle = useMemo(
    () => enableClickTitle && !show && !disabled,
    [enableClickTitle, show, disabled],
  );

  return (
    <CardContext.Provider value={{ active: isActive, expand: show }}>
      <CollapseCardActionContext.Provider value={{ onClick }}>
        <div
          className={classNames("tlx-collapse-container", {
            active: isActive,
            enableClickTitle: addClickTitle,
          })}
          onClick={addClickTitle ? onClick : undefined}
        >
          <div className="tlx-collapse-container--title">
            {TitleChild}
            <div className="tlx-collapse-container--footer">
              <Button
                size="medium"
                fillMode={buttonMode}
                className="tlx-collapse-container--button"
                onClick={onButtonClick}
                icon={`arrow-chevron-${show ? "up" : "down"}`}
                disabled={disabled}
                aria-expanded={show}
                aria-label={label}
                aria-controls={wrapBody ? bodyId : undefined}
              />
            </div>
          </div>
          <If condition={show}>
            <Then>
              {wrapBody ? (
                <div id={bodyId} className="tlx-collapse-container--body">
                  {BodyChild}
                </div>
              ) : (
                BodyChild
              )}
            </Then>
          </If>
        </div>
      </CollapseCardActionContext.Provider>
    </CardContext.Provider>
  );
};

export const useCollapseState = () => {
  const context = React.useContext(CardContext);
  if (context === undefined) {
    throw new Error("useFieldDispatch must be used within a FormProvider");
  }
  return context;
};

export const useCollapseAction = () => {
  const context = React.useContext(CollapseCardActionContext);
  if (context === undefined) {
    throw new Error("useCollapseAction must be used within a CollapseCard");
  }
  return context;
};

export default CollapseCard;
